import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {Popup, Button, Icon} from 'semantic-ui-react';

import {connect} from 'react-redux';
import _ from 'lodash';
import {CollaborationWorker} from 'framework/collaboration/collaboration.office.js';
import {
  SAVE_ENTITY_NETWORK_ERROR_MSG,
  SAVE_ENTITY_NETWORK_ERROR_TITLE,
} from 'framework/classes/concurrentEditProcessor';
import DynamicComponentActions from 'go/actions/dynamicComponent';
import CollaborationActions from 'go/actions/api/collaboration';
import {
  CW_AWARENESS_UPDATE,
  CW_NETWORK_STATUS_ABORT,
  CW_NETWORK_STATUS_CONNECTED,
  CW_NETWORK_STATUS_ERROR,
  CW_STATUS_MODIFICATION_EXCEPTION,
} from 'framework/collaboration/types';
import produce from 'immer';

export const mapStateToProps = (state) => {
  return {
    firstName: _.get(state, 'auth.firstName'),
    lastName: _.get(state, 'auth.lastName'),
    emailAdr: _.get(state, 'auth.emailAdr'),
    userId: _.get(state, 'auth.userId'),
    rooms: _.get(state, 'dynamicComponent.collaboration.rooms', {}),
    roomList: _.get(state, 'dynamicComponent.collaboration.roomList', ''),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  handleNetworkAbort: (errorArgs) => {
    dispatch(DynamicComponentActions.handleSaveError(errorArgs));
    dispatch(CollaborationActions.closeAllRooms());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(({firstName, lastName, emailAdr, userId, rooms, roomList, handleNetworkAbort}) => {
  const [cw, setCw] = useState();
  const [roomStatus, setRoomStatus] = useState({});
  const [otherUsers, setOtherUsers] = useState({});
  const [userHistory, setUserHistory] = useState({});
  const userHistoryRef = useRef(); // userHistoryRef allows the callback to always refrerence the latest value
  userHistoryRef.current = userHistory;
  const [collaboratorsPopup, setCollaboratorsPopup] = useState(false);

  // get access to collaboration worker
  useEffect(
    () => {
      load();
      async function load() {
        const collaborationWorker = await CollaborationWorker();
        setCw(collaborationWorker);
      }
    },
    [
      /* run once */
    ]
  );

  const ENTITY_TYPE_TO_NAME_MAP = {
    application: 'Application',
    amendment: 'Amendment',
    submitBSIRReport: 'Biannual Strategy Implementatoin Report',
  };
  const awarenessCallback = useCallback(
    (awarenessUpdate) => {
      switch (awarenessUpdate.type) {
        case CW_AWARENESS_UPDATE:
          // only update if different
          setOtherUsers((otherUsers) => {
            let knownUserIds = _.sortBy(
              _.map(_.get(otherUsers, awarenessUpdate.roomName), 'userId')
            );
            let newUserIds = _.sortBy(_.map(awarenessUpdate.remotes, 'userId'));
            if (!_.isEqual(knownUserIds, newUserIds)) {
              return produce(otherUsers, (draft) => {
                draft[awarenessUpdate.roomName] = awarenessUpdate.remotes;
              });
            } else {
              return otherUsers;
            }
          });
          setUserHistory((userHistory) => {
            // store history of seen users
            const HISTORY_WINDOW = 5 * 60 * 1000;
            const lastSeen = Date.now();
            const cutoffTime = lastSeen - HISTORY_WINDOW;
            const newUserHistory = _.keyBy(
              _.filter(userHistory, (user) => cutoffTime < user.lastSeen),
              'userId'
            );
            _.assign(
              newUserHistory,
              _.keyBy(
                _.map(awarenessUpdate.remotes, (user) => ({...user, lastSeen})),
                'userId'
              )
            );
            return newUserHistory;
          });
          break;
        case CW_NETWORK_STATUS_ABORT:
        case CW_STATUS_MODIFICATION_EXCEPTION:
          let networkErrorArgs = {
            title: SAVE_ENTITY_NETWORK_ERROR_TITLE,
            text: SAVE_ENTITY_NETWORK_ERROR_MSG,
          };
          if (
            awarenessUpdate.submission ||
            (awarenessUpdate.shouldBe && awarenessUpdate.found)
          ) {
            const titleEntityName =
              (awarenessUpdate.entityType &&
                ENTITY_TYPE_TO_NAME_MAP[awarenessUpdate.entityType]) ||
              'Form';
            const textEntityName =
              (awarenessUpdate.entityType &&
                ENTITY_TYPE_TO_NAME_MAP[awarenessUpdate.entityType]) ||
              'form';
            networkErrorArgs.title = `${titleEntityName} has been submitted`;
            networkErrorArgs.text = `This ${textEntityName} for ${
              awarenessUpdate.subGrantId || awarenessUpdate.grantId
            } has been submitted${
              userHistoryRef.current[awarenessUpdate.who]
                ? ` by ${userHistoryRef.current[awarenessUpdate.who].fullName}`
                : ''
            } and is no longer available for editing.`;
            networkErrorArgs.buttonText = 'Close';
            networkErrorArgs.alertType = 'none';
            networkErrorArgs.grantId = awarenessUpdate.grantId;
            networkErrorArgs.who = awarenessUpdate.who;
            networkErrorArgs.submission = awarenessUpdate.submission;
            networkErrorArgs.redirect = true;
          }
          /*
           * If current user is person that requested and obtained a submission lock,
           * then do not render error modal for them.
           */
          const dontDisplayModal =
            awarenessUpdate.type === CW_STATUS_MODIFICATION_EXCEPTION &&
            _.get(awarenessUpdate, 'lockedBy', '') === userId;
          if (!dontDisplayModal) {
            handleNetworkAbort(networkErrorArgs);
          }
          break;
        case CW_NETWORK_STATUS_CONNECTED:
        case CW_NETWORK_STATUS_ERROR:
          setRoomStatus((roomStatus) => ({
            ...roomStatus,
            [awarenessUpdate.roomName]: awarenessUpdate.type,
          }));
          break;
      }
    },
    [
      /* no dependencies because of useRef and set fns */
    ]
  );

  // setup callbacks
  useEffect(() => {
    if (!cw) return;
    cw.setAwarenessCallback(awarenessCallback);
  }, [cw]);
  // share local awareness
  useEffect(() => {
    if (!cw) return;
    cw.setLocalAwarenessState({
      fullName: `${firstName} ${lastName}`,
      emailAdr,
      userId,
    });
  }, [cw, firstName, lastName, emailAdr, userId, roomList]);
  // derive roommates list to render, and recentHistory user lookups
  const roommates = useMemo(
    () =>
      !roomList
        ? []
        : _.sortBy(
            _.keyBy(
              _.flatten(
                _.filter(otherUsers, (remoteUsers, documentName) =>
                  _.includes(roomList, documentName)
                )
              ),
              'userId'
            ),
            'fullName'
          ),
    [roomList, otherUsers]
  );

  const togglePopup = useCallback(() => setCollaboratorsPopup((prev) => !prev), []);
  const closePopup = useCallback(() => setCollaboratorsPopup(false));
  return (
    <>
      <div className="awareness-comp">
        {/* https://react.semantic-ui.com/modules/popup/#types-header */}
        {roommates.length > 0 ? (
          <div>
            <Popup
              open={collaboratorsPopup}
              on="click"
              onClose={closePopup} // Close the popup when the close event happens
              hideOnScroll={false}
              position="top center"
              content={
                <div className="awareness-popup">
                  <h4>
                    {`${roommates.length} Collaborator${
                      roommates.length === 1 ? '' : 's'
                    }`}
                  </h4>
                  <p>
                    {roommates.length === 1
                      ? 'You are the only collaborator in the room.'
                      : 'The following users are collaborating with you. You will be able to see any changes made to editable data.'}
                  </p>
                  <ul>
                    {roommates.map((user, index) => (
                      <li className="indent" key={user.userId}>
                        {`${user.fullName}, ${user.emailAdr}`}
                      </li>
                    ))}
                  </ul>
                  <br></br>
                  <br></br>
                  <a href="#" onClick={togglePopup} title="Close">
                    <Icon name="close" />
                    Close
                  </a>
                </div>
              }
              trigger={
                <Button
                  style={{background: 'green', color: 'white'}}
                  className="awareness-button"
                  onClick={togglePopup}
                >
                  {`${roommates.length} Collaborator${roommates.length === 1 ? '' : 's'}`}
                </Button>
              }
            />
          </div>
        ) : (
          <span></span>
        )}
      </div>
    </>
  );
});
